import React,{useState,useMemo}from"react";
import{X,Radio,AlertCircle,CheckCircle2,Monitor,Smartphone,ArrowLeft,Save,Pencil,Check,Users,Briefcase}from"lucide-react";
import{BLUE,fmtDate,needsStandupUpdate}from"../config";
import{StaBadge,PriBadge}from"./UI";

/* ─── helpers ──────────────────────────────────────────────────── */
function lastNWeekdays(n){
  const days=[];let d=new Date();
  while(days.length<n){
    d=new Date(d);d.setDate(d.getDate()-1);
    if(d.getDay()!==0&&d.getDay()!==6) days.push(d.toISOString().slice(0,10));
  }
  return days.reverse();
}

function taskAge(task){
  const hist=task.standupHistory||[];
  if(!hist.length) return 0;
  const last=[...hist].sort((a,b)=>b.date.localeCompare(a.date))[0].date;
  const today=new Date().toISOString().slice(0,10);
  return Math.max(0,Math.floor((new Date(today)-new Date(last))/86400000));
}

const AVATAR_COLORS=["#1D5FA5","#7c3aed","#0891b2","#15803d","#b45309","#c2410c","#9333ea","#d946ef","#0f766e","#dc2626","#ea580c","#64748b"];
function avatarColor(email){
  let h=0;for(const c of (email||"")) h=c.charCodeAt(0)+((h<<5)-h);
  return AVATAR_COLORS[Math.abs(h)%AVATAR_COLORS.length];
}
function initials(name){
  if(!name) return"?";
  const p=name.trim().split(/\s+/);
  return p.length>=2?p[0][0].toUpperCase()+p[p.length-1][0].toUpperCase():p[0].slice(0,2).toUpperCase();
}

const STA_BAR={completed:"#22c55e",in_progress:"#E89A0A",blocked:"#ef4444",todo:"#d1d5db",pending:"#3b82f6",declined:"#9ca3af"};

/* ─── DonutChart ────────────────────────────────────────────────── */
function DonutChart({segments,total}){
  const C=163.4,r=26,cx=37,cy=37;
  let offset=0;
  const arcs=segments.map((s,i)=>{
    const arc=s.value/Math.max(total,1)*C;
    const el=(
      <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth={10}
        strokeDasharray={`${arc.toFixed(1)} ${C}`} strokeDashoffset={(-offset).toFixed(1)}
        transform="rotate(-90 37 37)"/>
    );
    offset+=arc;
    return el;
  });
  return(
    <svg width="74" height="74" viewBox="0 0 74 74">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e5e7eb" strokeWidth={10}/>
      {arcs}
      <text x={cx} y={cy+5} textAnchor="middle" fontSize="15" fontWeight="800" fill="#111827">{total}</text>
    </svg>
  );
}

/* ─── MiniBar ───────────────────────────────────────────────────── */
function MiniBar({label,value,max,color,onClick,active}){
  const pct=Math.round(value/Math.max(max,1)*100);
  return(
    <div className={`flex items-center gap-1.5 rounded px-1 -mx-1 transition-colors ${onClick?"cursor-pointer hover:bg-blue-50":""} ${active?"bg-blue-100 ring-1 ring-blue-300":""}`}
      onClick={onClick}>
      <span className="text-[11px] text-slate-500 truncate shrink-0" style={{width:68}}>{label}</span>
      <div className="flex-1 h-2.5 bg-slate-200 rounded-sm overflow-hidden">
        <div className="h-full rounded-sm transition-all" style={{width:`${pct}%`,background:color||BLUE}}/>
      </div>
      <span className="text-[10px] text-slate-400 w-3.5 text-right shrink-0">{value}</span>
    </div>
  );
}

/* ─── VelocityBars ──────────────────────────────────────────────── */
function VelocityBars({days,counts,max}){
  const DAY_LETTER=["Su","M","T","W","Th","F","Sa"];
  return(
    <div className="flex items-end gap-1.5" style={{height:52}}>
      {days.map((d,i)=>{
        const p=d.split("-");
        const dow=new Date(+p[0],+p[1]-1,+p[2]).getDay();
        const h=Math.max(Math.round(counts[i]/Math.max(max,1)*38),counts[i]?3:0);
        return(
          <div key={d} className="flex-1 flex flex-col items-center gap-0.5">
            <span className="text-[9px] text-slate-500 font-semibold">{counts[i]||""}</span>
            <div className="w-full rounded-t-sm" style={{height:h,background:BLUE}}/>
            <span className="text-[9px] text-slate-400">{DAY_LETTER[dow]}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ─── GraphStrip ────────────────────────────────────────────────── */
function GraphStrip({standupTasks,users,open,onToggle,onUserClick,activeUserEmail}){
  const total=standupTasks.length||1;
  const done=standupTasks.filter(t=>t.sta==="completed").length;
  const active=standupTasks.filter(t=>t.sta==="in_progress").length;
  const blocked=standupTasks.filter(t=>t.sta==="blocked").length;
  const other=total-done-active-blocked;
  const priH=standupTasks.filter(t=>t.pri==="high").length;
  const priM=standupTasks.filter(t=>t.pri==="medium").length;
  const priL=standupTasks.filter(t=>t.pri==="low").length;

  // Tasks by member — exclude closed, group by responsible person (assignee if accepted, else owner)
  const CLOSED_STA=["completed","declined"];
  const byUser={};
  standupTasks.filter(t=>!CLOSED_STA.includes(t.sta)).forEach(t=>{
    const resp=t.asgn&&t.acc==="accepted"?t.asgn:t.own;
    byUser[resp]=(byUser[resp]||0)+1;
  });
  const allMembers=Object.entries(byUser).sort((a,b)=>b[1]-a[1]).map(([email,cnt])=>{
    const local=email.split("@")[0];
    const u=users.find(x=>x.email.split("@")[0].replace(/_/g,".")===local);
    const name=u?.displayName||local;
    const parts=name.trim().split(/\s+/);
    const shortName=parts.length>1?`${parts[0]} ${parts[parts.length-1][0]}`:parts[0];
    return{name:shortName,email,cnt,color:avatarColor(email)};
  });
  const maxUser=allMembers[0]?.cnt||1;

  const ageBuckets=[0,0,0,0,0];
  standupTasks.forEach(t=>{
    const a=taskAge(t);
    if(a<=2) ageBuckets[0]++;
    else if(a<=7) ageBuckets[1]++;
    else if(a<=14) ageBuckets[2]++;
    else if(a<=28) ageBuckets[3]++;
    else ageBuckets[4]++;
  });
  const maxAge=Math.max(...ageBuckets,1);

  const weekdays=lastNWeekdays(5);
  const velCounts=weekdays.map(d=>
    standupTasks.reduce((n,t)=>n+((t.standupHistory||[]).some(h=>h.date===d&&h.status==="completed")?1:0),0)
  );
  const maxVel=Math.max(...velCounts,1);

  return(
    <>
      <div className={`bg-white border-b border-slate-200 overflow-hidden transition-all duration-300 ${open?"opacity-100":"max-h-0 opacity-0 pointer-events-none"}`}
        style={open?{maxHeight:220}:{maxHeight:0}}>
        <div className="grid gap-3 px-6 py-3 items-start"
          style={{gridTemplateColumns:"200px 190px 180px 190px 1fr"}}>

          {/* Tasks by member */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Tasks by Member
              {activeUserEmail&&<button className="ml-2 text-blue-500 font-normal normal-case tracking-normal text-[9px] underline" onClick={()=>onUserClick&&onUserClick("")}>clear</button>}
            </p>
            <div className="flex flex-col gap-1.5 overflow-y-auto overflow-x-hidden" style={{maxHeight:138}}>
              {allMembers.map(u=><MiniBar key={u.email} label={u.name} value={u.cnt} max={maxUser} color={u.color}
                onClick={()=>onUserClick&&onUserClick(activeUserEmail===u.email?"":u.email)}
                active={activeUserEmail===u.email}/>)}
              {allMembers.length===0&&<p className="text-xs text-slate-300 italic">No data</p>}
            </div>
          </div>

          {/* Status donut */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Status</p>
            <div className="flex items-center gap-2.5">
              <DonutChart
                segments={[{value:done,color:"#22c55e"},{value:active,color:"#E89A0A"},{value:blocked,color:"#ef4444"},{value:other,color:"#d1d5db"}]}
                total={standupTasks.length}/>
              <div className="flex flex-col gap-1">
                {[["Done",done,"#22c55e"],["Active",active,"#E89A0A"],["Blocked",blocked,"#ef4444"],["Other",other,"#d1d5db"]].map(([l,v,c])=>(
                  <div key={l} className="flex items-center gap-1.5 text-xs">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{background:c}}/>
                    <span className="text-slate-500 flex-1">{l}</span>
                    <span className="font-bold text-slate-700">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Priority */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Priority</p>
            <div className="flex flex-col gap-1.5">
              <MiniBar label="High" value={priH} max={total} color="#ef4444"/>
              <MiniBar label="Medium" value={priM} max={total} color="#E89A0A"/>
              <MiniBar label="Low" value={priL} max={total} color="#3b82f6"/>
            </div>
          </div>

          {/* Task Age */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Task Age</p>
            <div className="flex flex-col gap-1.5">
              {[["0–2d",ageBuckets[0],"#22c55e"],["3–7d",ageBuckets[1],"#E89A0A"],["8–14d",ageBuckets[2],"#f97316"],["15–28d",ageBuckets[3],"#ef4444"],["28d+",ageBuckets[4],"#991b1b"]].map(([l,v,c])=>(
                <MiniBar key={l} label={l} value={v} max={maxAge} color={c}/>
              ))}
            </div>
          </div>

          {/* Velocity */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Velocity (completions/day)</p>
            <VelocityBars days={weekdays} counts={velCounts} max={maxVel}/>
          </div>

        </div>
      </div>
      {/* Toggle bar */}
      <div className="flex items-center gap-2 px-6 py-1.5 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-blue-50 select-none shrink-0"
        onClick={onToggle}>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          {open?"▴ Summary Charts — click to hide":"▾ Summary Charts — click to show"}
        </span>
      </div>
    </>
  );
}

/* ─── TaskLine (compact row in user card) ───────────────────────── */
function TaskLine({task,isManager,onDismiss,projects}){
  const stale=needsStandupUpdate(task);
  const blocked=task.sta==="blocked";
  const completed=task.sta==="completed";
  const age=taskAge(task);
  const bgCls=blocked?"bg-red-50 border-red-200":stale&&!completed?"bg-amber-50 border-amber-200":completed?"bg-emerald-50 border-emerald-200":"border-transparent";
  const canDismiss=isManager&&completed&&task.listType==="company";
  const jobNum=projects&&task.project?(projects.find(p=>String(p.spId||p.id)===String(task.project))?.jobNumber||null):null;
  return(
    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs min-h-[22px] ${bgCls}`}
      style={{boxShadow:`inset 2px 0 0 ${STA_BAR[task.sta]||"#d1d5db"}`}}>
      <span className={`flex-1 truncate font-medium min-w-0 ${completed?"text-slate-400 line-through":"text-slate-700"}`}>{task.title}</span>
      <div className="flex items-center gap-1 shrink-0">
        {task.pri==="high"&&!completed&&<span className="text-[10px] font-bold text-red-500">H</span>}
        {jobNum&&<span className="text-[9px] text-slate-400 font-mono">{jobNum}</span>}
        {stale&&!blocked&&!completed&&<span className="text-[10px] font-bold text-orange-500">!</span>}
        {blocked&&<span className="text-[10px]">⛔</span>}
        {completed&&<span className="text-[10px]">✓</span>}
        {age>=8&&!completed&&<span className={`text-[9px] ${age>=21?"text-red-400":"text-orange-400"}`}>🕐{age}d</span>}
        {canDismiss&&<input type="checkbox" title="Dismiss from standup" className="w-3 h-3 cursor-pointer shrink-0 accent-emerald-600"
          onChange={e=>{e.stopPropagation();onDismiss&&onDismiss(task);}}/>}
      </div>
    </div>
  );
}

/* ─── UserCard (grid item) ──────────────────────────────────────── */
function UserCard({user,todayTasks,density,onClick,isManager,onDismiss,projects}){
  const staleCount=todayTasks.filter(t=>needsStandupUpdate(t)).length;
  const hasBlocker=todayTasks.some(t=>t.sta==="blocked");
  const color=avatarColor(user.email);
  const borderTop=hasBlocker?"border-t-2 border-t-red-500":staleCount>0?"border-t-2 border-t-orange-400":"";
  return(
    <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${borderTop}`}
      onClick={onClick}>
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100 hover:bg-blue-50 transition-colors">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-[10px] shrink-0"
          style={{background:color}}>
          {initials(user.displayName||user.email)}
        </div>
        <span className="font-bold text-sm text-slate-800 flex-1 truncate">{user.displayName||user.email.split("@")[0]}</span>
        <span className="text-[10px] text-slate-400 shrink-0">{user.role||""}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {staleCount>0&&<span className="text-xs text-orange-500 font-bold flex items-center gap-0.5"><AlertCircle size={10}/>{staleCount}</span>}
          <span className="text-xs text-slate-500 font-bold">{todayTasks.length}</span>
        </div>
      </div>
      <div className={`px-2 py-1.5 flex flex-col gap-0.5 overflow-y-auto ${density==="tv"?"max-h-36":"max-h-52"}`}>
        {todayTasks.length===0
          ?<span className="text-xs text-slate-300 italic py-1 px-1">No standup tasks</span>
          :todayTasks.map(t=><TaskLine key={t.id} task={t} isManager={isManager} onDismiss={onDismiss} projects={projects}/>)
        }
      </div>
    </div>
  );
}

/* ─── DetailTaskRow (full expanded row in detail view) ─────────── */
function DetailTaskRow({task,onSaveNote,isManager,onDismiss,onUpdateTask,projects,users}){
  const stale=needsStandupUpdate(task);
  const[note,setNote]=useState("");
  const[newStatus,setNewStatus]=useState(task.sta);
  const[saving,setSaving]=useState(false);
  const[saved,setSaved]=useState(false);
  // inline edit state
  const[editOpen,setEditOpen]=useState(false);
  const[editSta,setEditSta]=useState(task.sta);
  const[editPri,setEditPri]=useState(task.pri);
  const[editBlock,setEditBlock]=useState(task.blockReason||"");
  const[editSaving,setEditSaving]=useState(false);
  const[editSaved,setEditSaved]=useState(false);
  const[currentTask,setCurrentTask]=useState(task);

  const today=new Date().toISOString().slice(0,10);
  const todayEntry=(currentTask.standupHistory||[]).find(h=>h.date===today);
  const prevHistory=[...(currentTask.standupHistory||[])].filter(h=>h.date!==today).sort((a,b)=>b.date.localeCompare(a.date));

  const handleSave=async()=>{
    if(!note.trim()) return;
    setSaving(true);
    const updated=await onSaveNote(currentTask,note,newStatus);
    if(updated) setCurrentTask(updated);
    setSaved(true);setSaving(false);setNote("");
  };

  const handleEditSave=async()=>{
    if(!onUpdateTask) return;
    setEditSaving(true);
    const patch={sta:editSta,pri:editPri,blockReason:editSta==="blocked"?editBlock:""};
    const updated=await onUpdateTask(currentTask,patch);
    if(updated) setCurrentTask(updated);
    setEditSaved(true);setEditSaving(false);
    setTimeout(()=>{setEditSaved(false);setEditOpen(false);},1200);
  };

  const rowBg=currentTask.sta==="blocked"&&!saved?"bg-red-50":stale&&!saved?"bg-amber-50":"bg-slate-50";
  const STA_OPTS=[["todo","To Do"],["in_progress","In Progress"],["completed","Completed"],["blocked","Blocked"],["pending","Pending"],["declined","Declined"]];
  const PRI_OPTS=[["high","🔴 High"],["medium","🟡 Medium"],["low","🔵 Low"]];

  return(
    <div className={`border border-slate-200 rounded-xl mb-2.5 overflow-hidden ${rowBg}`}
      style={{borderLeft:`3px solid ${STA_BAR[currentTask.sta]||"#d1d5db"}`}}>
      <div className="px-3 py-2.5">
        {/* Title row */}
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className={`font-semibold text-sm flex-1 min-w-0 ${currentTask.sta==="completed"?"text-slate-400 line-through":"text-slate-800"}`}>{currentTask.title}</span>
          {stale&&!saved&&currentTask.sta!=="completed"&&<span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded font-bold flex items-center gap-1"><AlertCircle size={8}/>Needs Update</span>}
          {currentTask.sta==="blocked"&&<span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">⛔ Blocked</span>}
          {currentTask.sta==="completed"&&<span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold flex items-center gap-1"><CheckCircle2 size={8}/>Completed</span>}
          {saved&&<span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold flex items-center gap-1"><CheckCircle2 size={8}/>Updated</span>}
          {/* Edit button — managers only */}
          {isManager&&onUpdateTask&&(
            <button onClick={()=>{setEditOpen(o=>!o);setEditSta(currentTask.sta);setEditPri(currentTask.pri);setEditBlock(currentTask.blockReason||"");setEditSaved(false);}}
              title="Edit task"
              className={`flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold transition-colors shrink-0
                ${editOpen?"bg-blue-100 border-blue-400 text-blue-700":"bg-slate-100 border-slate-300 text-slate-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600"}`}>
              <Pencil size={9}/>{editOpen?"Cancel Edit":"Edit"}
            </button>
          )}
          {isManager&&currentTask.sta==="completed"&&currentTask.listType==="company"&&(
            <button onClick={()=>onDismiss&&onDismiss(currentTask)}
              className="text-[10px] bg-slate-100 border border-slate-300 text-slate-500 hover:bg-red-50 hover:border-red-300 hover:text-red-600 px-2 py-0.5 rounded font-medium transition-colors shrink-0">
              Dismiss
            </button>
          )}
        </div>

        {/* Badges row */}
        <div className="flex items-center gap-2 flex-wrap">
          <StaBadge sta={currentTask.sta}/><PriBadge pri={currentTask.pri}/>
          {currentTask.dueDate&&<span className="text-xs text-slate-400">{fmtDate(currentTask.dueDate)}</span>}
        </div>

        {/* Owner / Assignee */}
        {(()=>{
          const dn=email=>{
            const u=(users||[]).find(x=>x.email.toLowerCase()===email?.toLowerCase());
            return u?.displayName||email?.split("@")[0]||"";
          };
          const ownerName=dn(currentTask.own);
          const hasAssignee=currentTask.asgn&&currentTask.asgn.toLowerCase()!==currentTask.own?.toLowerCase();
          const assigneeName=hasAssignee?dn(currentTask.asgn):"";
          return(
            <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
              <span><span className="font-semibold text-slate-400">Owner:</span> {ownerName}</span>
              {hasAssignee&&<span className="text-slate-300">→</span>}
              {hasAssignee&&<span><span className="font-semibold text-slate-400">Assignee:</span> {assigneeName}</span>}
            </div>
          );
        })()}

        {/* Project info */}
        {(()=>{
          const proj=currentTask.project&&projects?projects.find(p=>String(p.spId||p.id)===String(currentTask.project)):null;
          if(!proj)return null;
          return(
            <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500">
              <Briefcase size={10} className="shrink-0"/>
              <span className="truncate">{projectLabel(proj)}</span>
            </div>
          );
        })()}

        {/* Notes */}
        {currentTask.notes&&(
          <div className="mt-1.5 bg-white border border-slate-100 rounded px-2 py-1.5 text-[11px] text-slate-500 italic">
            {currentTask.notes}
          </div>
        )}

        {currentTask.sta==="blocked"&&currentTask.blockReason&&(
          <div className="mt-2 bg-red-50 border border-red-200 rounded px-2.5 py-1.5 text-xs text-red-700">⛔ {currentTask.blockReason}</div>
        )}

        {/* ── Inline edit panel ── */}
        {editOpen&&(
          <div className="mt-2.5 bg-white border border-blue-200 rounded-lg p-3 space-y-2.5">
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest flex items-center gap-1"><Pencil size={9}/>Quick Edit</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Status</label>
                <select value={editSta} onChange={e=>setEditSta(e.target.value)}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-blue-400">
                  {STA_OPTS.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Priority</label>
                <select value={editPri} onChange={e=>setEditPri(e.target.value)}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-blue-400">
                  {PRI_OPTS.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            {editSta==="blocked"&&(
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Block Reason</label>
                <input value={editBlock} onChange={e=>setEditBlock(e.target.value)} placeholder="What's blocking this task?"
                  className="w-full border border-slate-200 rounded px-2.5 py-1.5 text-xs focus:outline-none focus:border-blue-400 bg-white"/>
              </div>
            )}
            <div className="flex gap-1.5 justify-end pt-0.5">
              <button onClick={()=>setEditOpen(false)}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600">Cancel</button>
              <button onClick={handleEditSave} disabled={editSaving||editSaved}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-60 transition-all"
                style={{background:editSaved?"#22c55e":BLUE}}>
                {editSaving
                  ?<span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full"/>
                  :editSaved?<Check size={10}/>:<Save size={10}/>}
                {editSaved?"Saved!":"Save Changes"}
              </button>
            </div>
          </div>
        )}

        {/* Standup note section */}
        {todayEntry?(
          <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-2">
            <p className="text-[10px] font-bold text-emerald-700 mb-0.5">Today's update</p>
            <p className="text-xs text-slate-600">{todayEntry.note}</p>
          </div>
        ):(
          <div className="mt-2 flex gap-1.5">
            <select value={newStatus} onChange={e=>setNewStatus(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 text-xs bg-white shrink-0 focus:outline-none focus:border-blue-400">
              <option value="todo">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="blocked">Blocked</option>
            </select>
            <input value={note} onChange={e=>setNote(e.target.value)} placeholder="What's the update?"
              className="flex-1 border border-slate-200 rounded px-2.5 py-1 text-xs focus:outline-none focus:border-blue-400 bg-white"
              onKeyDown={e=>{if(e.key==="Enter"&&note.trim()) handleSave();}}/>
            <button onClick={handleSave} disabled={saving||!note.trim()}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-white text-xs font-semibold disabled:opacity-40 shrink-0"
              style={{background:BLUE}}>
              {saving?<span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full"/>:<Save size={10}/>}Save
            </button>
          </div>
        )}

        {/* Previous history */}
        {prevHistory.length>0&&(
          <div className="mt-2 space-y-1">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Previous</p>
            {prevHistory.slice(0,3).map((h,i)=>(
              <div key={i} className="bg-white border border-slate-100 rounded px-2.5 py-1.5">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[10px] font-semibold text-slate-400">{fmtDate(h.date)}</span>
                  <StaBadge sta={h.status}/>
                </div>
                {h.note&&<p className="text-xs text-slate-500">{h.note}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── UserDetail (drill-down panel) ────────────────────────────── */
function UserDetail({user,todayTasks,yesterdayTasks,onSaveNote,onBack,isManager,onDismiss,onUpdateTask,projects,users}){
  const color=avatarColor(user.email);
  const staleCount=todayTasks.filter(t=>needsStandupUpdate(t)).length;
  const blockerCount=todayTasks.filter(t=>t.sta==="blocked").length;
  const activeCount=todayTasks.filter(t=>t.sta!=="completed").length;
  return(
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-3 shrink-0 flex-wrap">
        <button onClick={onBack}
          className="flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 shrink-0">
          <ArrowLeft size={12}/>Back
        </button>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
            style={{background:color}}>
            {initials(user.displayName||user.email)}
          </div>
          <div>
            <p className="font-extrabold text-slate-800">{user.displayName||user.email.split("@")[0]}</p>
            <p className="text-[11px] text-slate-400">{user.role||user.email}</p>
          </div>
        </div>
        <div className="flex gap-2 ml-auto flex-wrap">
          {[["Active",activeCount,"#1e40af"],["Stale",staleCount,"#c2410c"],["Blocked",blockerCount,"#dc2626"],["Done",yesterdayTasks.length,"#15803d"]].map(([l,v,c])=>(
            <div key={l} className="text-center px-3 py-1 border border-slate-200 rounded-lg bg-slate-50 shrink-0">
              <p className="text-xl font-extrabold leading-none" style={{color:c}}>{v}</p>
              <p className="text-[9px] text-slate-400 uppercase tracking-wide mt-0.5">{l}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Two columns: Yesterday / Today */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Yesterday */}
          <div>
            <div className="bg-emerald-50 border-b-2 border-emerald-200 px-3 py-1.5 rounded-t-lg flex items-center gap-1.5">
              <CheckCircle2 size={12} className="text-emerald-600"/>
              <span className="text-[10px] font-extrabold text-emerald-700 uppercase tracking-widest">Yesterday</span>
              <span className="ml-auto text-[10px] text-emerald-500 font-bold">{yesterdayTasks.length}</span>
            </div>
            <div className="border border-slate-200 border-t-0 rounded-b-lg bg-white p-2 min-h-20">
              {yesterdayTasks.length===0
                ?<p className="text-xs text-slate-300 italic py-3 text-center">No completed tasks</p>
                :yesterdayTasks.map(t=><DetailTaskRow key={t.id} task={t} onSaveNote={onSaveNote} isManager={isManager} onDismiss={onDismiss} onUpdateTask={onUpdateTask} projects={projects} users={users}/>)}
            </div>
          </div>
          {/* Today */}
          <div>
            <div className="bg-amber-50 border-b-2 border-amber-200 px-3 py-1.5 rounded-t-lg flex items-center gap-1.5">
              <Radio size={12} className="text-amber-600"/>
              <span className="text-[10px] font-extrabold text-amber-700 uppercase tracking-widest">Today</span>
              <span className="ml-auto text-[10px] text-amber-500 font-bold">{todayTasks.length}</span>
            </div>
            <div className="border border-slate-200 border-t-0 rounded-b-lg bg-white p-2 min-h-20">
              {todayTasks.length===0
                ?<div className="text-center py-8 text-slate-300">
                   <CheckCircle2 size={32} className="mx-auto mb-2 text-emerald-400"/>
                   <p className="text-xs font-semibold">All done!</p>
                 </div>
                :todayTasks.map(t=><DetailTaskRow key={t.id} task={t} onSaveNote={onSaveNote} isManager={isManager} onDismiss={onDismiss} onUpdateTask={onUpdateTask} projects={projects} users={users}/>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── ProjectCard (grid item for project view) ──────────────────── */
function projectLabel(proj){
  if(!proj)return"No Project";
  const parts=[];
  if(proj.customerName)parts.push(proj.customerName);
  if(proj.jobNumber)parts.push(proj.jobNumber);
  const prefix=parts.join(" · ");
  return prefix?`${prefix} – ${proj.projectName}`:(proj.projectName||"No Project");
}

function ProjectCard({proj,tasks,density,isManager,onDismiss,onClick}){
  const staleCount=tasks.filter(t=>needsStandupUpdate(t)).length;
  const hasBlocker=tasks.some(t=>t.sta==="blocked");
  const borderTop=hasBlocker?"border-t-2 border-t-red-500":staleCount>0?"border-t-2 border-t-orange-400":"";
  const label=proj?projectLabel(proj):"No Project";
  const color=avatarColor(proj?String(proj.spId||proj.id||""):"__none__");
  const abbr=(proj?.jobNumber||label).slice(0,3).toUpperCase();
  return(
    <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer ${borderTop}`}
      onClick={onClick}>
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100 hover:bg-blue-50 transition-colors">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-[10px] shrink-0"
          style={{background:color}}>{abbr}</div>
        <span className="font-bold text-sm text-slate-800 flex-1 truncate">{label}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {staleCount>0&&<span className="text-xs text-orange-500 font-bold flex items-center gap-0.5"><AlertCircle size={10}/>{staleCount}</span>}
          <span className="text-xs text-slate-500 font-bold">{tasks.length}</span>
        </div>
      </div>
      <div className={`px-2 py-1.5 flex flex-col gap-0.5 overflow-y-auto ${density==="tv"?"max-h-36":"max-h-52"}`}>
        {tasks.length===0
          ?<span className="text-xs text-slate-300 italic py-1 px-1">No standup tasks</span>
          :tasks.map(t=><TaskLine key={t.id} task={t} isManager={isManager} onDismiss={onDismiss}/>)
        }
      </div>
    </div>
  );
}

/* ─── ProjectDetail (drill-down panel for project view) ─────────── */
function ProjectDetail({proj,tasks,onSaveNote,onBack,isManager,onDismiss,onUpdateTask,projects,users}){
  const label=proj?projectLabel(proj):"No Project";
  const color=avatarColor(proj?String(proj.spId||proj.id||""):"__none__");
  const abbr=(proj?.jobNumber||label).slice(0,3).toUpperCase();
  const staleCount=tasks.filter(t=>needsStandupUpdate(t)).length;
  const blockerCount=tasks.filter(t=>t.sta==="blocked").length;
  const doneCount=tasks.filter(t=>t.sta==="completed").length;
  const activeCount=tasks.filter(t=>t.sta!=="completed").length;
  return(
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-3 shrink-0 flex-wrap">
        <button onClick={onBack}
          className="flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 shrink-0">
          <ArrowLeft size={12}/>Back
        </button>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0"
            style={{background:color}}>{abbr}</div>
          <div>
            <p className="font-extrabold text-slate-800">{label}</p>
            {proj?.customerName&&<p className="text-[11px] text-slate-400">{proj.customerName}{proj.jobNumber?` · ${proj.jobNumber}`:""}</p>}
          </div>
        </div>
        <div className="flex gap-2 ml-auto flex-wrap">
          {[["Active",activeCount,"#1e40af"],["Stale",staleCount,"#c2410c"],["Blocked",blockerCount,"#dc2626"],["Done",doneCount,"#15803d"]].map(([l,v,c])=>(
            <div key={l} className="text-center px-3 py-1 border border-slate-200 rounded-lg bg-slate-50 shrink-0">
              <p className="text-xl font-extrabold leading-none" style={{color:c}}>{v}</p>
              <p className="text-[9px] text-slate-400 uppercase tracking-wide mt-0.5">{l}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="max-w-3xl mx-auto">
          {tasks.length===0
            ?<p className="text-center text-slate-400 py-12">No standup tasks for this project.</p>
            :tasks.map(t=><DetailTaskRow key={t.id} task={t} onSaveNote={onSaveNote} isManager={isManager} onDismiss={onDismiss} onUpdateTask={onUpdateTask} projects={projects} users={users}/>)
          }
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN EXPORT ───────────────────────────────────────────────── */
export function StandupScreen({allTasks,users,cu,onSaveNote,onStandupToggle,onClose,onUpdateTask,projects}){
  const[density,setDensity]=useState("tv");
  const[chartsOpen,setChartsOpen]=useState(true);
  const[filterUser,setFilterUser]=useState("");
  const[filterPri,setFilterPri]=useState("");
  const[filterStale,setFilterStale]=useState(false);
  const[filterBlocker,setFilterBlocker]=useState(false);
  const[detailEmail,setDetailEmail]=useState(null);
  const[viewMode,setViewMode]=useState("user");
  const[detailProjectKey,setDetailProjectKey]=useState(null);

  const isManager=cu?.role==="Manager"||cu?.role==="Admin";
  const handleDismiss=task=>{onStandupToggle&&onStandupToggle(task,false);};

  const yDate=useMemo(()=>{const d=new Date();d.setDate(d.getDate()-1);return d.toISOString().slice(0,10);},[]);

  // All standup tasks — completed tasks always included (manager can dismiss company tasks)
  const standupTasks=useMemo(()=>allTasks.filter(t=>t.standup),[allTasks]);

  // Per-user data — built from tasks (not users list) so all owners appear
  // regardless of whether they're in allUsers or which email domain they use
  const CLOSED=["completed","declined"];
  const userData=useMemo(()=>{
    // Group non-closed standup tasks by the responsible person:
    // assignee if accepted, otherwise owner
    const ownerMap=new Map();
    allTasks.filter(t=>!CLOSED.includes(t.sta)).forEach(t=>{
      const responsible=(t.asgn&&t.acc==="accepted"?t.asgn:t.own).toLowerCase()||"unknown";
      if(!ownerMap.has(responsible)) ownerMap.set(responsible,[]);
      ownerMap.get(responsible).push(t);
    });
    return [...ownerMap.entries()].map(([em,tasks])=>{
      // Match user record by local part — handles @dacrobotics.com vs @dac-us.com
      const local=em.split("@")[0];
      const userRecord=(users||[]).find(u=>u.email.split("@")[0].replace(/_/g,".")===local);
      const displayName=userRecord?.displayName||local.replace(/\./g," ").replace(/\b\w/g,c=>c.toUpperCase());
      const todayTasks=[...tasks].sort((a,b)=>{
        const po={high:0,medium:1,low:2};
        return(needsStandupUpdate(b)?1:0)-(needsStandupUpdate(a)?1:0)||(po[a.pri]||1)-(po[b.pri]||1);
      });
      const yesterdayTasks=allTasks.filter(t=>{const resp=(t.asgn&&t.acc==="accepted"?t.asgn:t.own).toLowerCase();return resp===em&&(t.standupHistory||[]).some(h=>h.date===yDate);});
      return{email:userRecord?.email||em,displayName,role:userRecord?.role||"",todayTasks,yesterdayTasks};
    }).sort((a,b)=>a.displayName.localeCompare(b.displayName));
  },[allTasks,users,yDate]);

  // Apply filters
  const filteredUsers=useMemo(()=>userData.filter(u=>{
    if(filterUser&&u.email!==filterUser) return false;
    if(filterPri&&!u.todayTasks.some(t=>t.pri===filterPri)) return false;
    if(filterStale&&!u.todayTasks.some(t=>needsStandupUpdate(t))) return false;
    if(filterBlocker&&!u.todayTasks.some(t=>t.sta==="blocked")) return false;
    return true;
  }),[userData,filterUser,filterPri,filterStale,filterBlocker]);

  const projectData=useMemo(()=>{
    if(viewMode!=="project")return[];
    const byProj={};
    standupTasks.forEach(t=>{
      const key=t.project||"__none__";
      if(!byProj[key])byProj[key]=[];
      byProj[key].push(t);
    });
    return Object.entries(byProj).map(([key,tasks])=>{
      const proj=key==="__none__"?null:(projects||[]).find(p=>String(p.spId||p.id)===key);
      return{key,proj,tasks:tasks.sort((a,b)=>{
        const po={high:0,medium:1,low:2};
        return(needsStandupUpdate(b)?1:0)-(needsStandupUpdate(a)?1:0)||(po[a.pri]||1)-(po[b.pri]||1);
      })};
    }).sort((a,b)=>{
      if(!a.proj&&b.proj)return 1;
      if(a.proj&&!b.proj)return -1;
      return projectLabel(a.proj).localeCompare(projectLabel(b.proj));
    });
  },[standupTasks,viewMode,projects]);

  const totalStale=standupTasks.filter(t=>needsStandupUpdate(t)).length;
  const totalDone=standupTasks.filter(t=>t.sta==="completed").length;
  const totalActive=standupTasks.filter(t=>t.sta==="in_progress").length;
  const totalBlocked=standupTasks.filter(t=>t.sta==="blocked").length;

  const detailUser=detailEmail?userData.find(u=>u.email===detailEmail):null;

  return(
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-100">

      {/* ── Page header ── */}
      <div className="bg-white border-b border-slate-200 px-6 py-2.5 flex items-center gap-3 shrink-0 flex-wrap">
        <div>
          <p className="font-extrabold text-lg text-slate-800 flex items-center gap-2 leading-none">
            <Radio size={18} className="text-blue-600"/>Daily Standup
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}
          </p>
        </div>

        {/* Stats pills */}
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          {[
            {l:"Total",v:standupTasks.length,cls:"bg-blue-50 border-blue-200 text-blue-700",dot:"#3b82f6"},
            {l:"Done",v:totalDone,cls:"bg-emerald-50 border-emerald-200 text-emerald-700",dot:"#22c55e"},
            {l:"Active",v:totalActive,cls:"bg-amber-50 border-amber-200 text-amber-700",dot:"#E89A0A"},
            {l:"Blocked",v:totalBlocked,cls:"bg-red-50 border-red-200 text-red-700",dot:"#ef4444"},
            {l:"Stale",v:totalStale,cls:"bg-orange-50 border-orange-200 text-orange-700",dot:"#f97316"},
          ].map(s=>(
            <div key={s.l} className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${s.cls}`}>
              <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{background:s.dot}}/>
              {s.l}: {s.v}
            </div>
          ))}
        </div>

        {/* View mode toggle */}
        <div className="flex bg-slate-100 border border-slate-200 rounded-lg p-0.5 gap-0.5 shrink-0">
          {[["user","By Member",Users],["project","By Project",Briefcase]].map(([val,label,Icon])=>(
            <button key={val} onClick={()=>setViewMode(val)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${viewMode===val?"bg-white text-blue-600 shadow-sm":"text-slate-500 hover:text-slate-700"}`}>
              <Icon size={11}/>{label}
            </button>
          ))}
        </div>

        {/* Density toggle */}
        <div className="flex bg-slate-100 border border-slate-200 rounded-lg p-0.5 gap-0.5 shrink-0">
          {[["tv","TV",Monitor],["normal","Portrait",Smartphone]].map(([val,label,Icon])=>(
            <button key={val} onClick={()=>setDensity(val)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${density===val?"bg-white text-blue-600 shadow-sm":"text-slate-500 hover:text-slate-700"}`}>
              <Icon size={11}/>{label}
            </button>
          ))}
        </div>

        {/* Live pill */}
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-semibold shrink-0">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"/>Live
        </div>

        {/* End standup */}
        <button onClick={onClose}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 shrink-0">
          <X size={14}/>End Standup
        </button>
      </div>

      {/* ── Filters bar ── */}
      <div className="bg-white border-b border-slate-200 px-6 py-2 flex items-center gap-2.5 shrink-0 flex-wrap">
        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">User</span>
        <select value={filterUser} onChange={e=>setFilterUser(e.target.value)}
          className="bg-slate-50 border border-slate-200 rounded-md text-xs px-2 py-1 text-slate-600 focus:outline-none focus:border-blue-400">
          <option value="">All Members</option>
          {userData.map(u=><option key={u.email} value={u.email}>{u.displayName||u.email.split("@")[0]}</option>)}
        </select>
        <div className="w-px h-4 bg-slate-200 shrink-0"/>
        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Priority</span>
        {[{l:"All",v:""},{l:"🔴 High",v:"high"},{l:"🟡 Med",v:"medium"},{l:"🔵 Low",v:"low"}].map(p=>(
          <button key={p.v} onClick={()=>setFilterPri(p.v)}
            className={`px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all whitespace-nowrap
              ${filterPri===p.v?"bg-blue-600 border-blue-600 text-white":"bg-slate-50 border-slate-200 text-slate-500 hover:border-blue-400 hover:text-blue-600"}`}>
            {p.l}
          </button>
        ))}
        <div className="w-px h-4 bg-slate-200 shrink-0"/>
        <button onClick={()=>setFilterStale(s=>!s)}
          className={`px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all whitespace-nowrap
            ${filterStale?"bg-orange-500 border-orange-500 text-white":"bg-slate-50 border-slate-200 text-slate-500 hover:border-orange-400 hover:text-orange-600"}`}>
          ⚠️ Not Updated
        </button>
        <button onClick={()=>setFilterBlocker(s=>!s)}
          className={`px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all whitespace-nowrap
            ${filterBlocker?"bg-red-500 border-red-500 text-white":"bg-slate-50 border-slate-200 text-slate-500 hover:border-red-400 hover:text-red-600"}`}>
          ⛔ Blockers
        </button>
      </div>

      {/* ── Charts strip + toggle bar ── */}
      <GraphStrip standupTasks={standupTasks} users={users} open={chartsOpen} onToggle={()=>setChartsOpen(o=>!o)}
        onUserClick={email=>{setFilterUser(email);setChartsOpen(false);}}
        activeUserEmail={filterUser}/>

      {/* ── Main content: grid or detail ── */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {viewMode==="user"&&detailUser?(
          <UserDetail
            user={detailUser}
            todayTasks={detailUser.todayTasks}
            yesterdayTasks={detailUser.yesterdayTasks}
            onSaveNote={onSaveNote}
            onBack={()=>setDetailEmail(null)}
            isManager={isManager}
            onDismiss={handleDismiss}
            onUpdateTask={onUpdateTask}
            projects={projects}
            users={users}
          />
        ):viewMode==="project"&&detailProjectKey?(()=>{
          const pd=projectData.find(x=>x.key===detailProjectKey);
          return pd?(
            <ProjectDetail
              proj={pd.proj}
              tasks={pd.tasks}
              onSaveNote={onSaveNote}
              onBack={()=>setDetailProjectKey(null)}
              isManager={isManager}
              onDismiss={handleDismiss}
              onUpdateTask={onUpdateTask}
              projects={projects}
              users={users}
            />
          ):null;
        })():(
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {viewMode==="user"?(
              filteredUsers.length===0?(
                <div className="text-center py-20 text-slate-400">
                  <CheckCircle2 size={52} className="mx-auto mb-3 text-emerald-400 opacity-80"/>
                  <p className="font-bold text-xl text-slate-600">No standup tasks today!</p>
                  <p className="text-sm mt-1">{standupTasks.length===0?"Tag tasks with the standup icon to track them here.":"Try adjusting your filters."}</p>
                </div>
              ):(
                <div className={`grid gap-3 ${density==="tv"
                  ?"[grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]"
                  :"[grid-template-columns:repeat(auto-fill,minmax(360px,1fr))]"}`}>
                  {filteredUsers.map(u=>(
                    <UserCard key={u.email} user={u} todayTasks={u.todayTasks} density={density}
                      onClick={()=>setDetailEmail(u.email)} isManager={isManager} onDismiss={handleDismiss} projects={projects}/>
                  ))}
                </div>
              )
            ):(
              projectData.length===0?(
                <div className="text-center py-20 text-slate-400">
                  <Briefcase size={52} className="mx-auto mb-3 opacity-40"/>
                  <p className="font-bold text-xl text-slate-600">No standup tasks today!</p>
                  <p className="text-sm mt-1">Tag tasks with the standup icon to track them here.</p>
                </div>
              ):(
                <div className={`grid gap-3 ${density==="tv"
                  ?"[grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]"
                  :"[grid-template-columns:repeat(auto-fill,minmax(360px,1fr))]"}`}>
                  {projectData.map(({key,proj,tasks})=>(
                    <ProjectCard key={key} proj={proj} tasks={tasks} density={density} isManager={isManager} onDismiss={handleDismiss}
                      onClick={()=>setDetailProjectKey(key)}/>
                  ))}
                </div>
              )
            )}
          </div>
        )}
      </div>

    </div>
  );
}
